import { useQuery } from '@apollo/client';
import Button from '@mui/material/Button';
import gql from 'graphql-tag';
import { clone } from 'lodash';
import { isString } from 'lodash';
import { findIndex } from 'lodash';
import uniqueId from 'lodash/uniqueId';
import { forwardRef } from 'react';
import { useImperativeHandle } from 'react';
import { useCallback } from 'react';
import { useEffect } from 'react';
import { useState } from 'react';
import { useMemo } from 'react';
import React from 'react';
import ItemTable from '../../components/ItemTable';
import useMutationFHG from '../../fhg/components/data/useMutationFHG';
import DataLoadingAndErrors from '../../fhg/components/DataLoadingAndErrors';
import FHGTypography from '../../fhg/components/Typography';
import { removeOne } from '../../fhg/utils/Utils';
import EqualizationTimeEdit from './EqualizationTimeEdit';

const EQUALIZATION_TIME_INFO_FRAGMENT = gql`
  fragment equalizationTimeInfo on EqualizationTime {
    id
    label
    minutes
    operatorId
  }
`;

const EQUALIZATION_TIMES_ALL_WHERE_QUERY = gql`
   query getEqualizationTimesAllWhere($operatorId: [Int] ) {
      equalizationTimes:equalizationTime_AllWhere(equalizationTimeSearch: {operatorId: $operatorId}) {
         ...equalizationTimeInfo
      }
   }
   ${EQUALIZATION_TIME_INFO_FRAGMENT}
`;

const EQUALIZATION_TIME_CREATE = gql`
   mutation equalizationTimeCreate($label: String!, $minutes: Int!, $operatorId: Int!)
   {
      equalizationTime: equalizationTime_Create(equalizationTime: {label: $label, minutes: $minutes, operatorId: $operatorId}) {
         ...equalizationTimeInfo
      }
   }
   ${EQUALIZATION_TIME_INFO_FRAGMENT}
`;

const EQUALIZATION_TIME_UPDATE = gql`
   mutation equalizationTimeUpdate($id: Int!, $label: String!, $minutes: Int!)
   {
      equalizationTime: equalizationTime_Update(equalizationTimeId: $id, equalizationTime: {label: $label, minutes: $minutes}) {
         ...equalizationTimeInfo
      }
   }
   ${EQUALIZATION_TIME_INFO_FRAGMENT}
`;

const EQUALIZATION_TIME_DELETE = gql`
   mutation equalizationTimeDelete($id: Int!) {
      equalizationTime_Delete(equalizationTimeId: $id)
   }
`;

const EqualizationTimeTable = forwardRef(
   function EqualizationTimeTable({ operatorId, onChange, initialItems }, ref) {
      const [equalizationTimeId, setEqualizationTimeId] = useState();
      const [equalizationTimeValue, setEqualizationTime] = useState();
      const [deletedEqualizationTimes, setDeletedEqualizationTimes] = useState([]);

      const { data, loading, error } = useQuery(EQUALIZATION_TIMES_ALL_WHERE_QUERY, { variables: { operatorId: [operatorId] }, skip: !operatorId });

      const [equalizationTimeCreate] = useMutationFHG(EQUALIZATION_TIME_CREATE, {
         refetchQueries: () => ([{ query: EQUALIZATION_TIMES_ALL_WHERE_QUERY, variables: { operatorId: [operatorId] } }])
      });
      const [equalizationTimeUpdate] = useMutationFHG(EQUALIZATION_TIME_UPDATE, {
         refetchQueries: () => ([{ query: EQUALIZATION_TIMES_ALL_WHERE_QUERY, variables: { operatorId: [operatorId] } }])
      });

      const [equalizationTimeDelete] = useMutationFHG(EQUALIZATION_TIME_DELETE, {
         refetchQueries: () => ([{ query: EQUALIZATION_TIMES_ALL_WHERE_QUERY, variables: { operatorId: [operatorId] } }])
      });

      const [equalizationTimes, setEqualizationTimes] = useState();

      const [showEdit, setShowEdit] = useState(false);
      const [isChanged, setIsChanged] = useState(false);

      useImperativeHandle(ref, () => ({
         async submit(operatorIdOverride) {
            const effectiveOperatorId = operatorIdOverride ?? operatorId;
            if (isChanged) {
               for (const equalizationTime of equalizationTimes) {
                  if (isString(equalizationTime.id)) {
                     // New record - CREATE
                     await equalizationTimeCreate({
                        variables: {
                           label: equalizationTime.label,
                           minutes: parseInt(equalizationTime.minutes, 10),
                           operatorId: effectiveOperatorId
                        }
                     });
                  } else if (equalizationTime.id > 0) {
                     // Existing record - UPDATE
                     await equalizationTimeUpdate({
                        variables: {
                           id: equalizationTime.id,
                           label: equalizationTime.label,
                           minutes: parseInt(equalizationTime.minutes, 10)
                        }
                     });
                  }
               }
               for (const deletedEqualizationTime of deletedEqualizationTimes) {
                  if (deletedEqualizationTime.id > 0) {
                     await equalizationTimeDelete({ variables: { id: deletedEqualizationTime.id } });
                  }
               }
               setDeletedEqualizationTimes([]);
               setIsChanged(false);
            }
         },
         refetch() {
            setShowEdit(false);
            setIsChanged(false);
         }
      }), [equalizationTimes, deletedEqualizationTimes, isChanged, equalizationTimeCreate, equalizationTimeUpdate, equalizationTimeDelete, operatorId]);

      useEffect(() => {
         if (!operatorId && initialItems) {
            setEqualizationTimes(clone(initialItems));
            if (initialItems.length > 0) {
               setIsChanged(true);
            }
         } else if (data?.equalizationTimes) {
            setEqualizationTimes(clone(data.equalizationTimes));
         }
      }, [data, operatorId, initialItems]);

      const onEditCompleted = useCallback((updatedValue) => {
         const cache = [...(equalizationTimes || [])];
         const index = findIndex(cache, { id: updatedValue.id });

         if (index < 0) {
            // If the item doesn't exist, add it
            setEqualizationTimes([...cache, updatedValue]);
         } else {
            // If the item exists, update it
            const updatedCache = [...cache];
            updatedCache[index] = updatedValue;
            setEqualizationTimes(updatedCache);
         }

         setEqualizationTimeId(updatedValue?.id);
         setEqualizationTime(updatedValue);
         setShowEdit(false);
         setIsChanged(true);
         onChange && onChange();
      }, [equalizationTimes, onChange]);

      const columns = useMemo(() => ([
         { Header: 'Label', accessor: 'label' },
         { Header: 'Minutes', accessor: 'minutes' },
      ]), []);

      const handleSelect = (equalizationTime, isDoubleClick = false) => {
         setEqualizationTime(equalizationTime);
         setEqualizationTimeId(equalizationTime?.id);
         setShowEdit(isDoubleClick);
      };

      const handleAdd = useCallback(() => {
         const newEqualizationTime = { id: uniqueId('-'), label: '', minutes: '', operatorId };
         setEqualizationTime(newEqualizationTime);
         setShowEdit(true);
      }, [operatorId]);

      const handleEdit = useCallback(() => {
         setShowEdit(true);
      }, []);

      const handleDelete = useCallback(() => {
         setIsChanged(true);
         if (!isString(equalizationTimeValue?.id)) {
            const cacheDeleted = clone(deletedEqualizationTimes);
            cacheDeleted.push(equalizationTimeValue);
            setDeletedEqualizationTimes(cacheDeleted);
         }

         const index = findIndex(equalizationTimes, { id: equalizationTimeValue?.id });

         if (index >= 0) {
            const cache = [...equalizationTimes];
            removeOne(cache, index);
            setEqualizationTimes(cache);
            onChange && onChange();
         } else {
            console.log('Could not find equalization time to delete', equalizationTimeValue);
         }
      }, [equalizationTimes, equalizationTimeValue, deletedEqualizationTimes, onChange]);

      if (error) {
         return <DataLoadingAndErrors error={error} isLoading={false} />;
      }

      return (
         <>
            <DataLoadingAndErrors error={error} isLoading={loading} />
            <ItemTable titleKey={'operator.equalizationTime.label'} columns={columns} items={equalizationTimes || []}
               onSelect={handleSelect}
               selectId={equalizationTimeId}
               showSearchTextField={false}>
               <Button color='primary' onClick={handleAdd} style={{ marginLeft: 8 }}>
                  <FHGTypography noWrap id={'operator.addEqualizationTime.button'} />
               </Button>
               <Button color='primary' onClick={handleEdit} style={{ marginLeft: 8 }} disabled={!equalizationTimeId}>
                  <FHGTypography noWrap id={'operator.editEqualizationTime.button'} disabled={!equalizationTimeId} />
               </Button>
               <Button color='primary' onClick={handleDelete} style={{ marginLeft: 8 }} disabled={!equalizationTimeId}>
                  <FHGTypography noWrap id={'operator.deleteEqualizationTime.button'} disabled={!equalizationTimeId} />
               </Button>
            </ItemTable>
            {showEdit && (
               <EqualizationTimeEdit
                  equalizationTimeValue={equalizationTimeValue}
                  onSubmit={onEditCompleted}
                  onClose={() => {
                     setShowEdit(false);
                  }}
               />
            )}
         </>
      );
   }
);

export default EqualizationTimeTable;
